// The consent layer's entry point, and the only module in this app that talks
// to the consent library.
//
// WHY IT IS A BUNDLE OF ITS OWN RATHER THAN A COMPONENT IN THE ROOT LAYOUT.
// Almost every page of this site is built ahead of time AND declares
// `csr = false` — the country pages, the passport-OCR hub and the field
// explorer ship no client bundle at all, deliberately, and nothing mounted from
// a layout ever runs on them. A banner that appears on the handful of pages
// with hydration and not on the whole search surface is not a consent layer; it
// is a consent layer with the front door missing. So this is built separately
// into one small classic script, loaded with `defer` from the document
// template, and it depends on nothing the framework does.
//
// It is also why the first-touch write is a request rather than a server hook:
// a prerendered file is served before any hook of this application runs, so the
// server never sees the landing at all and only the browser can tell it.
//
// WHAT IT MAY DO BEFORE AN ANSWER: read its own consent cookie, and nothing
// else. No storage is written, no request is made and no third-party anything
// is loaded until a category is granted.

import * as CookieConsent from "vanilla-cookieconsent";
// Imported so the bundler extracts it into `consent.css` beside this script.
// It is NOT injected by the bundle — `loadStylesheet` below fetches it once this
// deferred script runs, which keeps it off the render-blocking path and out of
// the prerendered HTML the page guards walk.
import "vanilla-cookieconsent/dist/cookieconsent.css";
// After the library's sheet, and it has to stay after it: these rules correct
// that sheet and win only by coming later at the same specificity.
import "./overrides.css";
import { ANALYTICS_CATEGORY } from "./categories.ts";
import { consentConfig } from "./config.ts";
import {
  CONSENT_DEFAULTS,
  CONSENT_GRANTED,
  CONSENT_WITHDRAWN,
  clickEvent,
  GA_ID_ATTRIBUTE,
  type GaIdentity,
  gaAllowedOn,
  gaConfig,
  gaScriptSrc,
  looksPersonal,
  readIdentity,
  readMeasurementId,
  sanitizeEvent,
  toGaPageLocation,
} from "./google-analytics.ts";
import { MEASUREMENT_FLAG_ATTRIBUTE, measurementConfigured } from "./measurement.ts";
import { installRecorderGuard } from "./recorder-guard.ts";
import { replayAllowed } from "./routes.ts";
import {
  eventNameForTarget,
  RECORDER_HOST_URL,
  RECORDER_SRC,
  TRACKER_HOST_URL,
  TRACKER_SRC,
  toMeasurementPath,
} from "./tracker.ts";

/**
 * The bucket the browser tracker reports into — never the one the reverse proxy
 * emits server-side page views to. A visit that produced both would otherwise
 * be counted twice, and the ratio between the two buckets IS the measured
 * consent rate.
 *
 * Derived rather than allocated (`websiteIdFor("consented")` in the API), so
 * every environment produces this same value and nothing is copied by hand. The
 * test beside this file fails if the derivation stops producing it.
 */
export const CONSENTED_WEBSITE_ID = "6f0853e0-eca6-5506-a866-8b5c5d618aa5";

/** The global the tracker is told to hand every outgoing payload to first. */
const BEFORE_SEND = "__dcBeforeSend";

/** What the banner's answer is counted as. Mirrors the server's own union. */
type Choice = "accept" | "reject" | "dismiss" | "change";

/**
 * This script's own address, captured while it is executing.
 *
 * The stylesheet sits beside it, and "beside it" is not the same path in both
 * places that serve this bundle: the web app serves it at the origin root and
 * the documentation build serves its own copy under the documentation's mount.
 * Deriving the one from the other means neither template has to state it.
 */
const SELF =
  typeof document !== "undefined" && document.currentScript instanceof HTMLScriptElement
    ? document.currentScript
    : null;

const SELF_SRC = SELF?.getAttribute("src") || "/consent.js";

// WHETHER THERE IS ANYTHING TO LOAD. The tracker and the recorder are served
// from `/s` of this origin, and a deployment that runs no measurement container
// answers 404 for every address under it. Asking anyway would put a failed
// request and a console error on the page of every visitor who pressed Accept,
// in exchange for nothing — so the two templates state the answer on this
// script's own tag and it is read once, here, before anybody has answered
// anything. Absent means no (measurement.ts).
//
// The banner is unaffected: it still asks, and the answer still reaches
// `/api/consent`, which is this app's own endpoint and exists either way.
const MEASUREMENT_AVAILABLE = measurementConfigured(SELF?.getAttribute(MEASUREMENT_FLAG_ATTRIBUTE));

// THE GOOGLE ANALYTICS PROPERTY, if this deployment has one. Carried on this
// script's own tag by whichever template emitted it, like the flag above, and
// null when the attribute is absent, empty or not shaped like an ID — which is
// the state of every local run and of the dev preview, so none of them ever
// sends anything to Google.
const GA_ID = readMeasurementId(SELF?.getAttribute(GA_ID_ATTRIBUTE));

// Whether the tag reports in its debug view, for checking an installation by
// hand: opening any page with `ga_debug=1` in its address turns it on for that
// page and nothing else. Held in memory; the parameter itself is cut from every
// address the tag is told (`toMeasurementPath` keeps campaign parameters only).
const GA_DEBUG =
  typeof location !== "undefined" && new URLSearchParams(location.search).get("ga_debug") === "1";

interface UmamiPayload {
  url?: string;
  referrer?: string;
  [key: string]: unknown;
}

interface Umami {
  track(payload?: unknown): void;
}

type EventParams = Readonly<Record<string, string | number>>;

declare global {
  interface Window {
    umami?: Umami;
    // The hook the tracker hands every outgoing payload to. Named by the
    // string in `BEFORE_SEND`, which is what the script tag carries; the two
    // are asserted to agree in the unit suite, because a typo here would
    // silently turn the guard below into nothing.
    __dcBeforeSend?: (type: string, payload: UmamiPayload) => UmamiPayload | undefined;
    // Set by the inline script at the top of src/app.html; see `signalSettled`.
    __docCheapConsentSettled?: () => void;
    // The tag's own command queue and entry point, created here and nowhere
    // else, and only once measurement has been allowed.
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    // Events a page raised before this bundle ran, and who is signed in
    // (src/lib/consent/track.ts).
    __dcAnalyticsPending?: [string, EventParams][];
    __dcAnalyticsIdentity?: GaIdentity;
  }
}

// WHERE THIS VISIT STARTED, held in memory and nowhere else.
//
// Captured the moment this script runs, before anybody has answered anything,
// because by the time consent is given `document.referrer` may have been
// replaced by a client-side navigation. In memory rather than in session
// storage: writing a value to the device before consent is precisely what this
// layer exists not to do, and the published guidance puts session storage
// in scope alongside cookies.
//
// THE COVERAGE BOUNDARY, stated rather than glossed: these are the referrer and
// address of the page the banner was ANSWERED on. When the answer is given on
// the page the visitor arrived at — which is the overwhelming majority, because
// the banner appears on the first page — that is the landing. When somebody
// reads three pages first and then accepts, it is the third page. Nothing
// storage-free can do better, and inventing a better-looking number by stashing
// the first one before consent would be the violation.
const arrival = {
  referrer: typeof document === "undefined" ? "" : document.referrer,
  landing: typeof location === "undefined" ? "/" : `${location.pathname}${location.search}`,
};

let trackerLoaded = false;
let googleLoaded = false;
let lastGoogleLocation = "";
let recorderLoaded = false;
let analyticsGranted = false;
let lastPath = "";
// Set by the close control's own listener, in the capture phase, and read by
// the answer that the library then records. See `wireDismiss`.
let closedByIcon = false;

/** Loads a first-party script once, with the attributes the tool reads. */
function loadScript(src: string, attributes: Record<string, string>): void {
  const element = document.createElement("script");
  element.src = src;
  element.defer = true;
  for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value);
  document.head.appendChild(element);
}

/**
 * Tells the server what was answered, and — when measurement was allowed — what
 * this visit arrived from, so it can write the first-touch cookie.
 *
 * One request for both, because they are one event: the answer is what makes
 * the cookie lawful and the arrival values are only meaningful beside it. It is
 * `keepalive` so an answer given on the way out of the page is still recorded,
 * and a failure is silent — a banner that reported an error because a
 * measurement did not land would be worse than the missing measurement.
 */
function reportAnswer(choice: Choice, granted: readonly string[]): void {
  const allowed = granted.includes(ANALYTICS_CATEGORY);
  const body = JSON.stringify({
    choice,
    ...(allowed ? { referrer: arrival.referrer, landing: arrival.landing } : {}),
  });
  try {
    void fetch("/api/consent", {
      method: "POST",
      keepalive: true,
      headers: { "content-type": "application/json" },
      body,
    }).catch(() => {});
  } catch {
    // An answer is recorded or it is not; it never breaks the page.
  }
}

/**
 * One deliberate page view, with the address reduced to a path.
 *
 * Deliberate rather than automatic because the tool's own automatic tracking
 * has a known defect on this framework: it reports the absolute address on a
 * client-side navigation, which splits one page's count across two keys. So its
 * automatic page view is switched off (`data-auto-pageview`) and this is the
 * only thing that sends one — while `data-auto-track` stays on, because that is
 * what installs the tool's web-vitals collection.
 */
function pageview(path: string): void {
  const url = toMeasurementPath(path, location.origin);
  if (url === lastPath) return;
  const previous = lastPath;
  lastPath = url;
  window.umami?.track((payload: UmamiPayload) => ({
    ...payload,
    url,
    ...(previous === "" ? {} : { referrer: previous }),
  }));
}

function startTracker(): void {
  // Nothing serves it here, so nothing asks for it. Stated at the loader rather
  // than at the caller because there are two callers and a third would be
  // written by somebody who never read this comment.
  if (trackerLoaded || !MEASUREMENT_AVAILABLE) return;
  trackerLoaded = true;

  // Every payload the tracker sends — this app's page views, its own custom
  // events and its web-vitals reports alike — passes through here on the way
  // out. Two jobs: reduce the address to a path, and refuse to send anything at
  // all once consent has been withdrawn. Returning nothing cancels the send,
  // which is the tool's own documented contract for this hook.
  window.__dcBeforeSend = (_type: string, payload: UmamiPayload) => {
    if (!analyticsGranted) return undefined;
    const url = typeof payload.url === "string" ? payload.url : "/";
    return { ...payload, url: toMeasurementPath(url, location.origin) };
  };

  loadScript(TRACKER_SRC, {
    "data-website-id": CONSENTED_WEBSITE_ID,
    "data-host-url": TRACKER_HOST_URL,
    // No automatic page view: `pageview()` above is the only thing that sends
    // one. Automatic tracking itself stays on — it is what installs the
    // web-vitals observers and the history hook that flushes them per page.
    "data-auto-pageview": "false",
    "data-performance": "true",
    // Belt and braces beside `toMeasurementPath`: whatever the tool builds an
    // address from itself carries no query.
    "data-exclude-search": "true",
    "data-before-send": BEFORE_SEND,
  });

  // The tool's script defines `window.umami` as it runs. The first page view is
  // sent once it exists rather than on a timer.
  const wait = window.setInterval(() => {
    if (!window.umami) return;
    window.clearInterval(wait);
    pageview(location.pathname + location.search);
    startRecorder();
  }, 50);
  window.setTimeout(() => window.clearInterval(wait), 10_000);
}

/**
 * The session recorder, on the public surfaces only and never in the cabinet.
 *
 * THE RECORDER CANNOT BE STOPPED. The pinned build exposes no handle at all —
 * it starts itself when its script runs and records until the page goes away —
 * and this site is one client-side router, so a visitor who accepted on the
 * landing page and then clicked through to the cabinet would carry a running
 * recorder onto pages that render a customer's end-user document fields. Two
 * things hold that shut, and both are needed:
 *
 *   - the script is only ever loaded from an address the route rule allows;
 *   - every batch it tries to POST is dropped by `installRecorderGuard` when
 *     the page it would describe is not one. That is the permanent guarantee,
 *     because it is checked at the moment of sending rather than at the moment
 *     of starting.
 *
 * The landing demo, which takes a real document from a visitor, is blocked
 * inside the page instead — see `REPLAY_BLOCK_CLASS`.
 *
 * It is started after the tracker rather than beside it because it refuses to
 * send anything until the tracker has a session to attach to.
 */
function startRecorder(): void {
  if (recorderLoaded || !MEASUREMENT_AVAILABLE) return;
  if (!analyticsGranted || !replayAllowed(location.pathname)) return;
  recorderLoaded = true;
  loadScript(RECORDER_SRC, {
    "data-website-id": CONSENTED_WEBSITE_ID,
    "data-host-url": RECORDER_HOST_URL,
  });
}

/**
 * Custom events, by one attribute, delegated once.
 *
 * The handler reads a NAME and has no way to read a value — see
 * `eventNameForTarget`. That is the guarantee rather than a convention: the one
 * thing a browser tracker on this product must never carry is a field out of
 * somebody's document, and the way to be sure is to leave no route for one.
 */
function wireEvents(): void {
  document.addEventListener(
    "click",
    (event) => {
      if (!analyticsGranted) return;
      const target = event.target as Element | null;
      const name = eventNameForTarget(target);
      if (name === null) return;
      window.umami?.track(name);
      const google = clickEvent(name);
      if (google !== null) googleEvent(google.name, google.params);
    },
    { capture: true, passive: true },
  );
}

/**
 * Tells a refusal made with the banner's close control apart from one made with
 * its Reject button.
 *
 * The library wires that control to "accept necessary only", so both produce
 * the same stored answer and the same callback — but they are not the same
 * behaviour, and the published estimate of how many people answer a banner at
 * all is old enough that the three counts have to be real from the first day.
 * Capture phase, so the flag is set before the library's own handler runs and
 * records the answer.
 */
function wireDismiss(): void {
  document.addEventListener(
    "click",
    (event) => {
      const target = event.target as Element | null;
      if (target?.closest(".cm__btn--close") ?? null) closedByIcon = true;
    },
    { capture: true, passive: true },
  );
}

/** The Google tag's entry point, once it exists. */
function gtag(...args: unknown[]): void {
  window.gtag?.(...args);
}

/** Whether a Google command may be sent from this page right now. */
function googleActive(): boolean {
  return googleLoaded && analyticsGranted && gaAllowedOn(location.pathname);
}

/**
 * Loads the Google tag — after consent, on an allowed page, once.
 *
 * The consent declaration comes first and says everything is denied; the
 * update right after it grants measurement and nothing else. The order is the
 * vendor's: a default has to precede the first command it governs. Because the
 * tag is never requested before this point, a visitor who refuses, or who has
 * not answered, causes no request to Google of any kind.
 */
function startGoogle(): void {
  if (googleLoaded || GA_ID === null || !analyticsGranted) return;
  if (!gaAllowedOn(location.pathname)) return;
  googleLoaded = true;
  window.dataLayer = window.dataLayer ?? [];
  // The tag reads the queue as `arguments` objects, not arrays: its own
  // snippet pushes `arguments`, and an array is silently ignored.
  window.gtag = function gtagQueue() {
    // biome-ignore lint/complexity/noArguments: the tag's queue format is the arguments object itself.
    window.dataLayer?.push(arguments);
  };
  gtag("consent", "default", CONSENT_DEFAULTS);
  gtag("consent", "update", CONSENT_GRANTED);
  gtag("js", new Date());
  // The address first, with `set`, and never in the configuration: a value in
  // `config` would outrank every later `set` and pin every event to this page
  // (google-analytics.ts, `gaConfig`).
  gtag("set", { page_location: toGaPageLocation(location.href, location.origin) });
  gtag(
    "config",
    GA_ID,
    gaConfig({
      identity: readIdentity(window.__dcAnalyticsIdentity),
      debug: GA_DEBUG,
    }),
  );
  loadScript(gaScriptSrc(GA_ID), {});
  googlePageview(location.pathname + location.search);
  // Anything a page raised before this bundle was ready — a key created on the
  // request that rendered the page, say — goes now, in order.
  const pending = window.__dcAnalyticsPending ?? [];
  window.__dcAnalyticsPending = [];
  for (const [name, params] of pending) googleEvent(name, params);
}

/**
 * One Google page view, with the address reduced before it leaves.
 *
 * `set` as well as the event, so everything the tag measures on its own from
 * here on — scrolling, outbound clicks, downloads — is filed under the reduced
 * address rather than the one in the address bar.
 */
function googlePageview(path: string): void {
  if (!googleActive()) return;
  const pageLocation = toGaPageLocation(path, location.origin);
  if (pageLocation === lastGoogleLocation) return;
  const previous = lastGoogleLocation;
  lastGoogleLocation = pageLocation;
  const title = looksPersonal(document.title) ? undefined : document.title;
  gtag("set", {
    page_location: pageLocation,
    ...(title === undefined ? {} : { page_title: title }),
  });
  gtag("event", "page_view", {
    page_location: pageLocation,
    ...(previous === "" ? {} : { page_referrer: previous }),
    ...(title === undefined ? {} : { page_title: title }),
  });
}

/** One Google event, if it is on the list and carries nothing it may not. */
function googleEvent(name: string, params: EventParams): void {
  if (!googleActive()) return;
  const event = sanitizeEvent(name, params);
  // The reduced address of the page it happened on, stated on the event itself
  // so nothing the tag carried over from an earlier page can stand in for it.
  if (event !== null) {
    gtag("event", event.name, {
      ...event.params,
      page_location: lastGoogleLocation || toGaPageLocation(location.href, location.origin),
    });
  }
}

/**
 * A late identity — the page learned who is signed in after the tag started.
 * Only the two fields the configuration carries, through the same reading.
 */
function googleIdentify(value: GaIdentity): void {
  window.__dcAnalyticsIdentity = value;
  if (!googleActive() || GA_ID === null) return;
  const identity = readIdentity(value);
  gtag("set", {
    user_id: identity.userId,
    ...(identity.internal ? { traffic_type: "internal" } : {}),
  });
}

/**
 * Stops Google on a withdrawal, from the very next command.
 *
 * The vendor's own off switch (`ga-disable-<id>`) as well as the consent
 * update, so nothing is sent even from a tag already running. Its cookies are
 * erased by the consent library, which the measurement category's `autoClear`
 * names them to (config.ts).
 */
function stopGoogle(): void {
  if (GA_ID === null) return;
  (window as unknown as Record<string, unknown>)[`ga-disable-${GA_ID}`] = true;
  if (googleLoaded) gtag("consent", "update", CONSENT_WITHDRAWN);
  lastGoogleLocation = "";
}

/** Switches measurement on or off to match the answer now in force. */
function applyConsent(granted: readonly string[]): void {
  const allowed = granted.includes(ANALYTICS_CATEGORY);
  const changed = allowed !== analyticsGranted;
  analyticsGranted = allowed;
  if (allowed) {
    startTracker();
    if (GA_ID !== null) {
      (window as unknown as Record<string, unknown>)[`ga-disable-${GA_ID}`] = false;
      if (googleLoaded && changed) {
        gtag("consent", "update", CONSENT_GRANTED);
        googlePageview(location.pathname + location.search);
      }
    }
    startGoogle();
  }
  // Withdrawal does not unload a script that is already running; the guard in
  // `before-send` is what stops the tracker sending, from the very next event,
  // and the tool writes no storage of its own for the library to have to clear.
  if (!allowed && changed) {
    lastPath = "";
    stopGoogle();
  }
}

export async function start(): Promise<void> {
  wireEvents();
  wireDismiss();
  installRecorderGuard(window, () => location.pathname);
  await loadStylesheet();

  const shown = CookieConsent.run(
    consentConfig({
      onReady: (granted) => {
        applyConsent(granted);
      },
      onFirstAnswer: (granted) => {
        const allowed = granted.includes(ANALYTICS_CATEGORY);
        reportAnswer(allowed ? "accept" : closedByIcon ? "dismiss" : "reject", granted);
        closedByIcon = false;
      },
      onChanged: (granted) => {
        applyConsent(granted);
        reportAnswer("change", granted);
        closedByIcon = false;
      },
    }),
  );

  // The page's client router, when there is one, hands its navigations here.
  // On the pages built ahead of time with no client bundle there is no router
  // and no second page view, which is correct: there is no second page.
  window.__dcAnalytics = {
    pageview: (path: string) => {
      if (!analyticsGranted) return;
      pageview(path);
      // A visitor who accepted in the cabinet and then walked out to a public
      // page gets a recording from there on; one who walks the other way keeps
      // a running recorder whose batches the transport guard drops.
      startRecorder();
      // The same for the Google tag: a visitor who accepted on a page it never
      // runs on starts it on the first page it may.
      startGoogle();
      googlePageview(path);
    },
    // Events a page raises itself (src/lib/consent/track.ts). Held until
    // measurement is allowed and the tag has started; dropped if it never is.
    event: (name: string, params: EventParams) => {
      if (googleActive()) {
        googleEvent(name, params);
        return;
      }
      if (analyticsGranted) return;
      const pending = window.__dcAnalyticsPending ?? [];
      if (pending.length < 20) pending.push([name, params]);
      window.__dcAnalyticsPending = pending;
    },
    identify: googleIdentify,
  };

  // The banner is on screen, or there is none to show: the page's hydration
  // may start (see `signalSettled`).
  void shown.then(signalSettled, signalSettled);
}

/** The class the library puts on the root element while its banner is open. */
const BANNER_OPEN = "show--consent";

/** The banner's wrapper once the library has let it fade in. */
const BANNER_VISIBLE = "#cc-main .cc--anim";

/** The longest hydration waits for the banner to become visible. */
const BANNER_WAIT_CAP_MS = 1000;

/**
 * Tells the document template that the consent layer is done painting.
 *
 * A prerendered page holds its own module downloads until the first paint AND
 * this call (src/app.html), because on a phone the banner's paragraph is often
 * the largest thing on the page and a download started before it paints is one
 * that paint is measured as waiting for. Absent on a page with no such
 * template — the documentation serves this same bundle — so the call is
 * optional.
 *
 * `run()` resolving is not yet the banner on screen: the library builds it
 * transparent and adds the class that fades it in a tenth of a second later
 * (`BANNER_VISIBLE`), and the browser records the banner's paint only once
 * that fade has put it on screen. So when a banner is open this waits for the
 * class, then for whichever comes first of the browser reporting a new largest
 * paint (the banner, where it is the largest thing on the page) or the fade
 * finishing (where it is not). When no banner is open there is nothing to wait
 * for. Every wait is bounded, so a library that changes how it shows the banner
 * delays hydration by `BANNER_WAIT_CAP_MS` at most rather than for good.
 */
function signalSettled(): void {
  let signalled = false;
  const settle = () => {
    if (signalled) return;
    signalled = true;
    requestAnimationFrame(() => {
      setTimeout(() => window.__docCheapConsentSettled?.(), 0);
    });
  };
  if (!document.documentElement.classList.contains(BANNER_OPEN)) {
    settle();
    return;
  }
  const cap = setTimeout(settle, BANNER_WAIT_CAP_MS);
  const painted = () => {
    clearTimeout(cap);
    settle();
  };
  whenBannerVisible(() => {
    try {
      new PerformanceObserver((_list, observer) => {
        observer.disconnect();
        painted();
      }).observe({ type: "largest-contentful-paint" });
    } catch {
      // No largest-paint reporting in this browser: the fade's end alone.
    }
    document
      .querySelector(BANNER_VISIBLE)
      ?.addEventListener("transitionend", painted, { once: true });
  });
}

/** Calls back once the banner's fade-in class is on, now or later. */
function whenBannerVisible(callback: () => void): void {
  if (document.querySelector(BANNER_VISIBLE) !== null) {
    callback();
    return;
  }
  const observer = new MutationObserver(() => {
    if (document.querySelector(BANNER_VISIBLE) === null) return;
    observer.disconnect();
    callback();
  });
  observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ["class"] });
}

/**
 * The banner's stylesheet, fetched once the page is already interactive.
 *
 * Resolved rather than awaited-forever: a stylesheet that fails to load must
 * not be the reason a visitor never gets asked, so the banner is shown either
 * way and an unstyled banner is the failure mode rather than an absent one.
 */
function loadStylesheet(): Promise<void> {
  return new Promise((resolve) => {
    const href = SELF_SRC.replace(/consent\.js(\?.*)?$/, "consent.css");
    // A preload of the same address is a fetch, not a stylesheet: only a sheet
    // already applied means there is nothing left to do.
    if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return resolve();
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.addEventListener("load", () => resolve(), { once: true });
    link.addEventListener("error", () => resolve(), { once: true });
    document.head.appendChild(link);
  });
}

// AS SOON AS THE DOCUMENT IS PARSED, not after it has loaded.
//
// This script is loaded with `defer`, so by the time it runs the document is
// parsed and the page's own content has been handed to the renderer; nothing
// here blocks it, and the banner is a fixed overlay, so it shifts nothing when
// it arrives. It used to wait for `load` and then for an idle moment, which
// kept the library's work out of the first frames — and made the banner the
// last large thing to paint. On a phone its paragraph is the largest block on
// most pages, so the browser recorded it as the page's largest contentful
// paint, one to three seconds after everything a reader came for was already
// there. Shown straight away it paints with the page and stops being the late
// element the measurement waits for. What a visitor is asked, and what runs
// before they answer, is unchanged.
// A layer that fails before it has asked anything still lets the page hydrate;
// the failure itself stays unhandled, so it is reported like any other.
void start().catch((error: unknown) => {
  signalSettled();
  throw error;
});
